import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from '@angular/router';
import { NotificationSearchModel, SignalRNotificationModel } from 'app/core/models';
import { SignalRNotificationService } from 'app/core/services/signalr.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-notification-panel',
    templateUrl: './notification-panel.component.html',
    styleUrls: ['./notification-panel.component.css']
})
export class NotificationPanelComponent implements OnInit, OnDestroy {

    //#region Fields & Properties
    @Input() open = false;
    @Output() closePanel = new EventEmitter<void>();

    notifications: SignalRNotificationModel[] = [];
    unreadCount = 0;
    activeTab: 'all' | 'read' | 'unread' = 'all';

    private readonly destroy$ = new Subject<void>();
    searchModel: NotificationSearchModel = new NotificationSearchModel();
    //#endregion

    //#region Constructor
    constructor(
        private _router: Router,
        private _signalRService: SignalRNotificationService
    ) { }
    //#endregion

    //#region Methods
    ngOnInit(): void {
        this._signalRService.notifications$
            .pipe(takeUntil(this.destroy$))
            .subscribe(notifications => this.notifications = notifications);

        this._signalRService.unreadCount$
            .pipe(takeUntil(this.destroy$))
            .subscribe(count => this.unreadCount = count);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    close(): void { this.closePanel.emit(); }

    clearAll(): void { this._signalRService.clearAll(); }

    setTab(tab: 'all' | 'read' | 'unread'): void { this.activeTab = tab; }

    markAsRead(notification: SignalRNotificationModel, event: Event): void {
        event.stopPropagation();
        if (!notification.isRead) this._signalRService.markAsRead(notification.id);
        if (notification.redirectionUrl) {
            this._router.navigateByUrl(notification.redirectionUrl);
            this.close();
        }
    }

    loadMore(event: Event): void {
        const target = event.target as HTMLElement;
        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
        if (isNearBottom) this._signalRService.loadMoreNotifications();
    }

    trackByNotificationId(index: number, notification: SignalRNotificationModel): number {
        return notification.id;
    }

    isToday(notification: SignalRNotificationModel): boolean {
        const timestamp = notification.timestamp ? new Date(notification.timestamp) : null;
        if (!timestamp || Number.isNaN(timestamp.getTime())) return false;
        return timestamp.toDateString() === new Date().toDateString();
    }

    getIconClass(notification: SignalRNotificationModel): string {
        switch (notification.type) {
            case 'success': return 'fa-check';
            case 'warning': return 'fa-triangle-exclamation';
            case 'error': return 'fa-xmark';
            default: return 'fa-bell';
        }
    }

    getIconTone(notification: SignalRNotificationModel): string {
        switch (notification.type) {
            case 'success': return 'tone-success';
            case 'warning': return 'tone-warning';
            case 'error': return 'tone-danger';
            default: return 'tone-info';
        }
    }

    formattedDateTime(notification: SignalRNotificationModel): string {
        const timestamp = notification.timestamp ? new Date(notification.timestamp) : null;
        if (!timestamp || Number.isNaN(timestamp.getTime())) return '';

        return new Intl.DateTimeFormat('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit'
        }).format(timestamp);
    }

    onSearch(value: string): void {
        this.searchModel.searchText = value;
        this._signalRService.reloadNotifications(this.searchModel);
    }

    // ── Counts ──
    get readCount(): number {
        return this.notifications.filter(n => n.isRead).length;
    }

    // ── Filtered by active tab ──
    get filteredNotifications(): SignalRNotificationModel[] {
        switch (this.activeTab) {
            case 'read': return this.notifications.filter(n => n.isRead);
            case 'unread': return this.notifications.filter(n => !n.isRead);
            default: return this.notifications;
        }
    }

    get todayNotifications(): SignalRNotificationModel[] {
        return this.filteredNotifications.filter(n => this.isToday(n));
    }

    get olderNotifications(): SignalRNotificationModel[] {
        return this.filteredNotifications.filter(n => !this.isToday(n));
    }
    //#endregion
}
